/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Col, Row } from 'antd';
import IMAGE_DEFAULT from '@/assets/images/image_default.png';
import { MenuOutlined } from '@ant-design/icons';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
  getListBanner,
  getListBannerById,
  sortBannerSlider,
  watchListBannerById
} from '@/redux/slices/banner.slice';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useEffect, useState } from 'react';
import { useDrawer } from '@/hooks/drawer.hook';
import { Button } from '../button';
import { PAGINATION_PARAMS } from '@/constants/pagination.constant';

type BannerSliderDetailsTabsProps = {
  id: string;
};

export const BannerSliderDetailsTabs = ({
  id
}: BannerSliderDetailsTabsProps) => {
  const { closeDrawer } = useDrawer();
  const dispatch = useAppDispatch();
  const listBannerById = useAppSelector(watchListBannerById);
  const [newData, setNewData] = useState<Banner[]>(listBannerById);

  const handleSortBanner = async () => {
    const data = newData.map((item, index) => ({
      id: Number(item.id),
      sort_order: index,
      id_platform: Number(id)
    }));
    await dispatch(sortBannerSlider(data));
    await dispatch(
      getListBanner({
        offset: PAGINATION_PARAMS.offset,
        limit: PAGINATION_PARAMS.limit,
        sort_by: 'ASC'
      })
    );
    closeDrawer();
  };

  useEffect(() => {
    if (id) {
      dispatch(
        getListBannerById({
          platform: id,
          sort_by: 'ASC'
        })
      );
    }
  }, [dispatch, id]);

  useEffect(() => {
    setNewData(
      listBannerById
        .filter((banner) => banner.isActive)
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
    );
  }, [listBannerById]);

  return (
    <>
      <Row justify='space-around' css={headerTableStyle}>
        <Col span={4}></Col>
        <Col span={8}>
          <p>Hình banner</p>
        </Col>
        <Col span={12}>
          <p>Tên banner</p>
        </Col>
      </Row>
      <DragDropContext
        onDragEnd={(result: any) => {
          if (!result.destination) return;

          const items = Array.from(newData);
          const [reorderedItem] = items.splice(result.source.index, 1);
          items.splice(result.destination.index, 0, reorderedItem);

          setNewData(items);
        }}
      >
        <Droppable droppableId='droppable-list' direction='vertical'>
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              css={listContainerStyle}
            >
              {newData.map((item, index) => (
                <Draggable
                  key={item.id.toString()}
                  draggableId={item.id.toString()}
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        ...provided.draggableProps.style,
                        left: 0,
                        background: snapshot.isDragging ? '#f0f0f0' : 'white'
                      }}
                    >
                      <Row css={rowStyles(newData.length === index + 1)}>
                        <Col span={4}>
                          <MenuOutlined />
                        </Col>
                        <Col span={8}>
                          <img
                            src={item.imageUrl || IMAGE_DEFAULT}
                            alt={item.name}
                            css={bannerImageStyles}
                          />
                        </Col>
                        <Col span={12}>
                          <p>{item.name}</p>
                        </Col>
                      </Row>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Row justify='end' css={bottomStyles}>
        <Col>
          <Button
            type='primary'
            shape='round'
            htmlType='submit'
            onClick={handleSortBanner}
          >
            Cập nhật vị trí hiển thị
          </Button>
        </Col>
      </Row>
    </>
  );
};

const headerTableStyle = css`
  background-color: rgba(250, 250, 250, 1);
  padding: 1.2rem 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  .ant-col {
    padding: 0 1.2rem;
    &:not(&:first-of-type) {
      border-left: 1px solid rgba(0, 0, 0, 0.06);
    }
    &:nth-of-type(2) {
      text-align: center;
    }
  }
`;

const rowStyles = (isLastChild: boolean) => css`
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  margin-bottom: ${isLastChild ? '4rem' : '0'};
  .ant-col {
    padding: 1.2rem;
    text-align: center;
  }
  .ant-col:last-child {
    text-align: left;
  }
`;

const bottomStyles = css`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.4rem 2rem;
  background: var(--white-color);
  box-shadow: 0px -4px 6px rgba(0, 0, 0, 0.1);
`;

const bannerImageStyles = css`
  width: 5rem;
  height: 3.2rem;
  object-fit: cover;
  background: var(--white-color);
`;

const listContainerStyle = css`
  max-height: calc(100vh - 34rem);
  overflow-y: auto;
`;
